import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from 'components/ui/button';
import { Switch } from 'components/ui/switch';
import accessLocalStorage from 'services/localStorage';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';

// Constants for the Pay SDK Feature Flag global config. Mirrors the pattern
// established by the Static QR feature (`staticQR/index.tsx`) — the same
// `paySdkFeatureFlag` namespace is reused, with the `hosted_payments` channel
// scoping the loyalty toggle.
const SDK_COMPONENT = 'sdk';
const SDK_NAMESPACE = 'paySdkFeatureFlag';
const SDK_CHANNEL = 'hosted_payments';

// External link to the SellTreez "Loyalty Rewards Dollars" admin doc, surfaced
// in the description so merchants understand the upstream dependency.
const LOYALTY_REWARDS_DOLLARS_HELP_URL =
  'https://support.treez.io/en/articles/14459408-treez-loyalty-redeeming-loyalty-points-for-a-dollar-discount';

const HostedPaymentConfig = (): JSX.Element => {
  const {
    toFetchComponentSdkConfig,
    toUpdateComponentSdkConfig,
    paymentConfig,
  } = usePaymentConfig();

  const {
    componentSdkConfig: allSdkConfigs,
    componentSdkConfigUpdated,
    error,
  } = paymentConfig;
  const componentSdkConfig = allSdkConfigs?.[SDK_CHANNEL];

  const [enableLoyaltyRewards, setEnableLoyaltyRewards] = useState<
    boolean | null
  >(null);
  const [isSaving, setIsSaving] = useState(false);

  const justSavedRef = useRef(false);

  const entityId = accessLocalStorage.getEntityId();

  // Fetch once on mount. Switching store/entity remounts this view, so we
  // intentionally don't re-fetch on entityId change. Skip the call entirely
  // when there's no entity rather than firing an unscoped request.
  useEffect(() => {
    if (!entityId) return;
    toFetchComponentSdkConfig({
      component: SDK_COMPONENT,
      entityId,
      namespace: SDK_NAMESPACE,
      channel: SDK_CHANNEL,
    });
  }, []);

  // Sync local toggle state from the fetched server config.
  useEffect(() => {
    if (componentSdkConfig?.value?.enableLoyaltyRewards !== undefined) {
      setEnableLoyaltyRewards(
        Boolean(componentSdkConfig.value.enableLoyaltyRewards)
      );
    } else if (componentSdkConfig) {
      // Server returned config but the flag isn't set yet — default OFF so the
      // toggle has a definite state (rather than staying null forever).
      setEnableLoyaltyRewards(false);
    }
  }, [componentSdkConfig]);

  useEffect(() => {
    if (componentSdkConfigUpdated && justSavedRef.current) {
      toast.success('Hosted payment configuration saved');
      setIsSaving(false);
      justSavedRef.current = false;
    }
  }, [componentSdkConfigUpdated]);

  useEffect(() => {
    if (error?.updateComponentSdkConfig && justSavedRef.current) {
      toast.error("Couldn't save the configuration. Please try again.");
      setIsSaving(false);
      justSavedRef.current = false;
    }
  }, [error?.updateComponentSdkConfig]);

  // Dirty if the toggle has loaded AND differs from the persisted value.
  const isDirty =
    enableLoyaltyRewards !== null &&
    enableLoyaltyRewards !==
      Boolean(componentSdkConfig?.value?.enableLoyaltyRewards);

  const handleSave = () => {
    if (!componentSdkConfig || enableLoyaltyRewards === null) return;
    setIsSaving(true);
    justSavedRef.current = true;
    toUpdateComponentSdkConfig({
      component: SDK_COMPONENT,
      entityId,
      namespace: SDK_NAMESPACE,
      channel: SDK_CHANNEL,
      schemaVersion: componentSdkConfig.schemaVersion,
      // Preserve any other flags on the config object (iframe, enableStaticQR,
      // etc.) — only mutate the loyalty flag.
      value: {
        ...componentSdkConfig.value,
        enableLoyaltyRewards,
      },
    });
  };

  // No entity in context (unauthenticated state / mount race) — render an inert
  // fallback rather than a form bound to an unscoped config request.
  if (!entityId) {
    return (
      <p className="py-6 text-sm text-gray-blue-70">
        No store configured. Loyalty configuration unavailable.
      </p>
    );
  }

  return (
    <form
      className="py-6 flex flex-col items-start gap-5 max-w-3xl"
      onSubmit={(e) => e.preventDefault()}
    >
      <div>
        <h2 className="text-xl font-semibold text-gray-blue-80 mb-1">
          Hosted Payment Rewards
        </h2>
        <p className="text-sm text-gray-blue-70 m-0">
          Control loyalty point dollar redemption for hosted payment checkout.
        </p>
      </div>

      <div className="w-full flex justify-between items-start gap-8 rounded-lg border border-gray-blue-10 bg-gray-blue-1 px-6 py-5">
        <div className="flex-1">
          <div className="text-sm font-semibold text-gray-blue-80 mb-1">
            Enable Reward Redemption
          </div>
          <p className="text-sm text-gray-blue-70 m-0">
            Allow customers to apply their Treez Loyalty balance as a dollar
            discount during hosted payment checkout. Please note: This
            configuration is reliant on{' '}
            <a
              href={LOYALTY_REWARDS_DOLLARS_HELP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-dark-green-link underline hover:opacity-80"
            >
              Loyalty Rewards Dollars
            </a>{' '}
            being enabled.
          </p>
        </div>
        <Switch
          id="enable_loyalty_rewards"
          data-testid="enable_loyalty_rewards"
          checked={!!enableLoyaltyRewards}
          disabled={enableLoyaltyRewards === null}
          onCheckedChange={setEnableLoyaltyRewards}
          className="mt-1"
        />
      </div>

      <Button
        type="button"
        className="mt-2 w-44"
        disabled={!isDirty || isSaving}
        onClick={handleSave}
      >
        {isSaving ? 'Saving…' : 'Save'}
      </Button>
    </form>
  );
};

export default HostedPaymentConfig;
